"use client";

import { use, useState } from "react";
import { useIntersectionObserver } from "usehooks-ts";

import { getBokunPassengerCategoriesFromTripPassengers } from "~/lib/bokun";
import { getLinktivityPassengerCategoriesFromTripPassengers } from "~/lib/linktivity";
import { type ProductId } from "~/lib/tours";
import { useTripTour } from "~/lib/use-trip-tour";
import { trpcReact } from "~/trpc/client";
import { type RouterOutputs } from "~/trpc/types";

import { type Day } from "../_lib/trip";
import { TripContext } from "../_lib/trip-context";
import { TourBooking } from "./tour-booking";
import { TourInfo } from "./tour-info";

type TourConfig = RouterOutputs["tours"]["config"];
type DatoTour = Day["tours"][0]["tour"];

interface Props {
  date: Date;
  datoDayId: string;
  dayNumber: number;
  datoTour: DatoTour;
  productId: ProductId;
}

function ConfiguredTourCard({
  date,
  config,
  productId,
  datoTour,
  datoDayId,
  dayNumber,
}: Props & { config: TourConfig; datoDayId: string }) {
  const [page, setPage] = useState<"info" | "booking">("info");
  const trip = use(TripContext);
  if (!trip) {
    throw new Error("TripContext is not available");
  }
  const initialPassengerCategories =
    productId.type === "linktivity"
      ? getLinktivityPassengerCategoriesFromTripPassengers(
          trip.passengers,
          config.passengerCategories,
        )
      : getBokunPassengerCategoriesFromTripPassengers(
          trip.passengers,
          config.passengerCategories,
          config.minPassengersPerBooking ?? 1,
        );
  const { isFetching, choice, ...tripTourProps } = useTripTour({
    initialData: config,
    datoTourId: datoTour.id,
    datoDayId,
    initialPassengerCategories,
    productId,
    date,
  });
  return page === "info" ? (
    <TourInfo
      datoDayId={datoDayId}
      productId={productId}
      datoTour={datoTour}
      setPage={(value) => {
        setPage(value);
      }}
      isLoading={isFetching}
      choice={choice}
    />
  ) : (
    <TourBooking
      datoTour={datoTour}
      datoDayId={datoDayId}
      productId={productId}
      dayNumber={dayNumber}
      date={date}
      choice={choice}
      isFetching={isFetching}
      setPage={setPage}
      {...tripTourProps}
    />
  );
}

export function TourCard({ date, datoTour, productId, ...props }: Props) {
  const { isIntersecting, ref } = useIntersectionObserver({
    freezeOnceVisible: false,
    threshold: 0.1,
  });

  const configQuery = trpcReact.tours.config.useQuery(
    { productId },
    { enabled: isIntersecting },
  );

  const config = configQuery.data;

  if (config) {
    return (
      <div ref={ref} className="h-full">
        <ConfiguredTourCard
          {...props}
          productId={productId}
          config={config}
          date={date}
          datoTour={datoTour}
        />
      </div>
    );
  }

  return (
    <div ref={ref} className="h-full">
      <TourInfo
        {...props}
        datoTour={datoTour}
        productId={productId}
        isLoading={true}
      />
    </div>
  );
}
